import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// 12 hours of inactivity before logout
const INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;
// Show warning 5 minutes before logout
const WARNING_BEFORE_MS = 5 * 60 * 1000;
// Timer fires when warning should appear
const WARNING_TIMEOUT_MS = INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS;

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'focus'];
const LAST_ACTIVITY_KEY = 'mbt_last_activity';

const SessionTimeoutManager = () => {
  const { user, signOut } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isWarningActiveRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef?.current) clearTimeout(warningTimerRef?.current);
    if (logoutTimerRef?.current) clearTimeout(logoutTimerRef?.current);
    if (countdownIntervalRef?.current) clearInterval(countdownIntervalRef?.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    isWarningActiveRef.current = false;
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    await signOut();
  }, [signOut, clearAllTimers]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    isWarningActiveRef.current = false;

    warningTimerRef.current = setTimeout(() => {
      isWarningActiveRef.current = true;
      setShowWarning(true);
      setCountdown(300);

      // Start countdown display
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef?.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-logout after 5 more minutes
      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, WARNING_BEFORE_MS);
    }, WARNING_TIMEOUT_MS);
  }, [clearAllTimers, handleLogout]);

  const resetActivity = useCallback(() => {
    // Don't reset if warning is currently showing — user must explicitly click "Stay Signed In"
    if (isWarningActiveRef?.current) return;
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now()?.toString());
    startTimers();
  }, [startTimers]);

  const handleStaySignedIn = useCallback(() => {
    isWarningActiveRef.current = false;
    setShowWarning(false);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now()?.toString());
    startTimers();
  }, [startTimers]);

  // Check if session already expired on mount (e.g. tab was reopened after long time)
  useEffect(() => {
    if (!user) return;

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        handleLogout();
        return;
      }
      // Resume timers from where we left off
      const remaining = INACTIVITY_TIMEOUT_MS - elapsed;
      const warningRemaining = remaining - WARNING_BEFORE_MS;

      clearAllTimers();
      isWarningActiveRef.current = false;

      if (warningRemaining <= 0) {
        // Already in warning window
        const countdownSecs = Math.max(0, Math.floor(remaining / 1000));
        isWarningActiveRef.current = true;
        setShowWarning(true);
        setCountdown(countdownSecs);

        countdownIntervalRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef?.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        logoutTimerRef.current = setTimeout(() => {
          handleLogout();
        }, remaining);
      } else {
        warningTimerRef.current = setTimeout(() => {
          isWarningActiveRef.current = true;
          setShowWarning(true);
          setCountdown(300);

          countdownIntervalRef.current = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownIntervalRef?.current);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          logoutTimerRef.current = setTimeout(() => {
            handleLogout();
          }, WARNING_BEFORE_MS);
        }, warningRemaining);
      }
    } else {
      // First time — record activity and start timers
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now()?.toString());
      startTimers();
    }

    // Attach activity listeners
    const handleActivity = () => resetActivity();
    ACTIVITY_EVENTS?.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS?.forEach(event => window.removeEventListener(event, handleActivity));
      clearAllTimers();
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up when user logs out externally
  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      isWarningActiveRef.current = false;
    }
  }, [user, clearAllTimers]);

  if (!user || !showWarning) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timeDisplay = `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
        {/* Icon */}
        <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>

        {/* Title */}
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Your session is about to expire
        </h2>

        {/* Description */}
        <p className="text-sm text-gray-500 mb-4">
          You've been inactive for a while. For your security, you'll be automatically signed out in:
        </p>

        {/* Countdown */}
        <div className="text-4xl font-bold text-amber-600 mb-5 tabular-nums">
          {timeDisplay}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleStaySignedIn}
            className="w-full py-3 px-4 bg-primary text-white rounded-xl font-medium text-sm hover:opacity-90 transition-opacity"
          >
            Stay Signed In
          </button>
          <button
            onClick={handleLogout}
            className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
          >
            Sign Out Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutManager;
